import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  shareFirstConsultation,
  unshareFirstConsultation,
} from '@/api/firstConsultations/firstConsultationSharingApi';
import type { FirstConsultationShare } from '@/api/firstConsultations/firstConsultationSharingApi.types';
import { buildPatientFirstConsultationQueryKey } from '@/constants/queryKeys';

interface FirstConsultationSharingVariables {
  consultationId: string;
  patientId: string;
}

function useInvalidatePatientFirstConsultation() {
  const queryClient = useQueryClient();
  return function invalidate(
    _share: FirstConsultationShare,
    variables: FirstConsultationSharingVariables
  ) {
    queryClient.invalidateQueries({
      queryKey: buildPatientFirstConsultationQueryKey(variables.patientId),
    });
  };
}

export function useShareFirstConsultationMutation(): UseMutationResult<
  FirstConsultationShare,
  Error,
  FirstConsultationSharingVariables
> {
  const invalidate = useInvalidatePatientFirstConsultation();
  return useMutation({
    mutationFn: function share(variables: FirstConsultationSharingVariables) {
      return shareFirstConsultation(variables.consultationId);
    },
    onSuccess: invalidate,
  });
}

export function useUnshareFirstConsultationMutation(): UseMutationResult<
  FirstConsultationShare,
  Error,
  FirstConsultationSharingVariables
> {
  const invalidate = useInvalidatePatientFirstConsultation();
  return useMutation({
    mutationFn: function unshare(variables: FirstConsultationSharingVariables) {
      return unshareFirstConsultation(variables.consultationId);
    },
    onSuccess: invalidate,
  });
}
